import { useState, useCallback } from 'react';

export interface ApiConfig {
  baseUrl?: string;
  token?: string;
  onAuthError?: () => void;
}

export interface ApiError {
  message: string;
  status?: number;
  data?: unknown;
}

export interface UseApiOptions extends RequestInit {
  skipAuth?: boolean;
}

export function useApi(config: ApiConfig = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const baseUrl = config.baseUrl || import.meta.env.VITE_API_URL || 'http://localhost:8000';

  const request = useCallback(
    async <T = unknown>(
      endpoint: string,
      options: UseApiOptions = {},
    ): Promise<T> => {
      setLoading(true);
      setError(null);

      const { skipAuth = false, ...fetchOptions } = options;

      const headers = new Headers(fetchOptions.headers);

      // Always request JSON responses
      headers.set('Accept', 'application/json');

      if (!skipAuth && config.token) {
        headers.set('Authorization', `Token ${config.token}`);
      }

      if (!headers.has('Content-Type') && fetchOptions.body) {
        headers.set('Content-Type', 'application/json');
      }

      try {
        const response = await fetch(`${baseUrl}${endpoint}`, {
          ...fetchOptions,
          headers,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          
          // Check for authentication errors
          if ((response.status === 401 || response.status === 403) && config.onAuthError) {
            config.onAuthError();
          }
          
          throw {
            message:
              errorData?.detail ||
              `HTTP ${response.status}: ${response.statusText}`,
            status: response.status,
            data: errorData,
          };
        }

        const data = await response.json();
        return data;
      } catch (err) {
        const apiError: ApiError =
          err instanceof Error ? { message: err.message } : (err as ApiError);

        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [baseUrl, config.token, config.onAuthError],
  );

  const get = useCallback(
    <T = unknown>(endpoint: string, options?: UseApiOptions) =>
      request<T>(endpoint, { ...options, method: 'GET' }),
    [request],
  );

  const post = useCallback(
    <T = unknown>(endpoint: string, data?: unknown, options?: UseApiOptions) =>
      request<T>(endpoint, {
        ...options,
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
      }),
    [request],
  );

  const put = useCallback(
    <T = unknown>(endpoint: string, data?: unknown, options?: UseApiOptions) =>
      request<T>(endpoint, {
        ...options,
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined,
      }),
    [request],
  );

  const patch = useCallback(
    <T = unknown>(endpoint: string, data?: unknown, options?: UseApiOptions) =>
      request<T>(endpoint, {
        ...options,
        method: 'PATCH',
        body: data ? JSON.stringify(data) : undefined,
      }),
    [request],
  );

  const del = useCallback(
    <T = unknown>(endpoint: string, options?: UseApiOptions) =>
      request<T>(endpoint, { ...options, method: 'DELETE' }),
    [request],
  );

  return {
    loading,
    error,
    get,
    post,
    put,
    patch,
    delete: del,
    request,
  };
}
